import { rollRange } from './rules';

// `model` picks the 3D figure the viewport draws for the monster
export const MONSTER_TYPES = {
    giantRat: {
        name: 'Giant Rat',
        model: 'rat',
        maxHp: 12,
        ac: 1,
        speed: 18,
        accuracy: 5,
        attacks: 2,
        damage: [1, 4],
        xp: 40,
        gold: [0, 3],
        detectRange: 6,
    },
    goblin: {
        name: 'Goblin',
        model: 'goblin',
        maxHp: 22,
        ac: 2,
        speed: 15,
        accuracy: 6,
        attacks: 1,
        damage: [1, 6],
        xp: 90,
        gold: [5, 15],
        detectRange: 8,
    },
    orc: {
        name: 'Orc',
        model: 'orc',
        maxHp: 60,
        ac: 3,
        speed: 12,
        accuracy: 8,
        attacks: 1,
        damage: [2, 8],
        xp: 300,
        gold: [15, 40],
        detectRange: 8,
    },
    skeleton: {
        name: 'Skeleton',
        model: 'skeleton',
        maxHp: 38,
        ac: 5,
        speed: 10,
        accuracy: 9,
        attacks: 1,
        damage: [1, 10],
        xp: 220,
        gold: [10, 25],
        detectRange: 10,
    },
};

// Relative spawn weights per monster type
const SPAWN_TABLE = [['giantRat', 4], ['goblin', 3], ['orc', 2], ['skeleton', 1]];

let nextMonsterId = 1;

export function createMonster(type, x, y, rng = Math.random) {
    const def = MONSTER_TYPES[type];
    return {
        ...def,
        id: 'm' + (nextMonsterId++),
        type,
        x,
        y,
        hp: def.maxHp,
        gold: rollRange(def.gold[0], def.gold[1], rng),
        stunned: 0,
        hitCount: 0,
    };
}

// Floor, open doors and chests can be walked over by monsters
export function isWalkableTile(tile) {
    return tile === 0 || tile === 4 || tile === 6 || tile === 8 || tile === 11;
}

const NEIGHBOURS = [[0, -1], [1, 0], [0, 1], [-1, 0]];

// Breadth-first distances from a point over walkable tiles
function distanceField(map, [sx, sy], blocked = new Set()) {
    const dist = map.map((row) => row.map(() => Infinity));
    dist[sy][sx] = 0;
    const queue = [[sx, sy]];
    while (queue.length) {
        const [x, y] = queue.shift();
        for (const [dx, dy] of NEIGHBOURS) {
            const nx = x + dx;
            const ny = y + dy;
            if (ny < 0 || ny >= map.length || nx < 0 || nx >= map[0].length) continue;
            if (dist[ny][nx] !== Infinity) continue;
            if (!isWalkableTile(map[ny][nx]) || blocked.has(nx + ',' + ny)) continue;
            dist[ny][nx] = dist[y][x] + 1;
            queue.push([nx, ny]);
        }
    }
    return dist;
}

function pickSpawnType(rng) {
    const total = SPAWN_TABLE.reduce((sum, [, weight]) => sum + weight, 0);
    let roll = rng() * total;
    for (const [type, weight] of SPAWN_TABLE) {
        roll -= weight;
        if (roll < 0) return type;
    }
    return SPAWN_TABLE[0][0];
}

// Scatter monsters over reachable floor tiles, away from the party's start
// and from each other
// A reachable floor tile a fair walk from the party's start, for placing
// things like a trainer's desk
export function findFloorSpot(map, start, minDistance = 5, rng = Math.random) {
    const dist = distanceField(map, start);
    const candidates = [];
    for (let y = 0; y < map.length; y++) {
        for (let x = 0; x < map[0].length; x++) {
            if (map[y][x] === 0 && dist[y][x] !== Infinity && dist[y][x] >= minDistance) {
                candidates.push([x, y]);
            }
        }
    }
    if (!candidates.length) return null;
    return candidates[Math.floor(rng() * candidates.length)];
}

export function spawnMonsters(map, start, rng = Math.random, count = 4 + Math.floor(rng() * 3)) {
    const dist = distanceField(map, start);
    const candidates = [];
    for (let y = 0; y < map.length; y++) {
        for (let x = 0; x < map[0].length; x++) {
            if (map[y][x] === 0 && dist[y][x] !== Infinity && dist[y][x] >= 6) {
                candidates.push([x, y]);
            }
        }
    }
    const monsters = [];
    while (monsters.length < count && candidates.length) {
        const [x, y] = candidates.splice(Math.floor(rng() * candidates.length), 1)[0];
        if (monsters.some((m) => Math.abs(m.x - x) + Math.abs(m.y - y) < 3)) continue;
        monsters.push(createMonster(pickSpawnType(rng), x, y, rng));
    }
    return monsters;
}

export function monsterAt(monsters, x, y) {
    return monsters.find((m) => m.x === x && m.y === y && m.hp > 0);
}

// Close enough to fight: next to the party, or already on its square
export function isWithinReach(monster, [px, py]) {
    return Math.abs(monster.x - px) + Math.abs(monster.y - py) <= 1;
}

// Heading (as used by the player camera) that faces from one tile to another
export function headingToward([fx, fy], [tx, ty]) {
    const dx = tx - fx;
    const dy = ty - fy;
    if (Math.abs(dx) >= Math.abs(dy)) {
        return dx > 0 ? 270 : 90;
    }
    return dy > 0 ? 180 : 0;
}

// Grid step for moving forward along a heading (matches the movement controls)
export function headingVector(heading) {
    return { 0: [0, -1], 90: [-1, 0], 180: [0, 1], 270: [1, 0] }[heading];
}

// Walls and closed doors block line of sight; everything else can be seen past
export function isSeeThroughTile(tile) {
    return tile !== undefined && tile !== 1 && tile !== 2 && tile !== 3 && tile !== 5;
}

// The nearest monster straight ahead of the party within missile range
export function findTargetInSight(monsters, map, [px, py], heading, range = 5) {
    const [dx, dy] = headingVector(heading);
    for (let distance = 1; distance <= range; distance++) {
        const x = px + dx * distance;
        const y = py + dy * distance;
        if (y < 0 || y >= map.length || x < 0 || x >= map[0].length) return null;
        if (!isSeeThroughTile(map[y][x])) return null;
        const monster = monsterAt(monsters, x, y);
        if (monster) return { monster, distance };
    }
    return null;
}

// Every monster that can smell the party takes one step toward it
export function stepMonsters(monsters, map, playerCoords) {
    const moved = [];
    monsters.forEach((monster, index) => {
        if (monster.stunned > 0) {
            moved.push({ ...monster, stunned: monster.stunned - 1 });
            return;
        }
        if (isWithinReach(monster, playerCoords)) {
            moved.push(monster);
            return;
        }
        const blocked = new Set([
            ...moved.map((m) => m.x + ',' + m.y),
            ...monsters.slice(index + 1).map((m) => m.x + ',' + m.y),
        ]);
        // Distances measured from the party, so the monster just walks downhill
        const dist = distanceField(map, playerCoords, blocked);
        const here = dist[monster.y][monster.x];
        if (here === Infinity || here > monster.detectRange) {
            moved.push(monster);
            return;
        }
        let best = null;
        for (const [dx, dy] of NEIGHBOURS) {
            const nx = monster.x + dx;
            const ny = monster.y + dy;
            if (ny < 0 || ny >= map.length || nx < 0 || nx >= map[0].length) continue;
            if (nx === playerCoords[0] && ny === playerCoords[1]) continue;
            if (dist[ny][nx] < here && (!best || dist[ny][nx] < best.d)) {
                best = { x: nx, y: ny, d: dist[ny][nx] };
            }
        }
        moved.push(best ? { ...monster, x: best.x, y: best.y } : monster);
    });
    return moved;
}
